import { ScriptLoader } from '@thiememeulenhoff/js-sdk';
import { NAVIGATE_EVENT } from '@thiememeulenhoff/types';
import { PlayerError, PlayerState } from './read-speaker-state.enum';
import { config } from './read-speaker.config';

declare global {
  interface Window {
    rsConf: any; // eslint-disable-line
    rspkr: any; // eslint-disable-line
  }
}

export interface ReadSpeakerEvent {
  element: HTMLElement;
  event: PlayerState | PlayerError;
}

interface ReadSpeakerSubscription {
  element: HTMLElement;
  callback: (event: ReadSpeakerEvent) => void;
}

interface ScrollSubscription {
  element: Element;
  contentElement: Element;
  callback: () => void;
}

export class ReadSpeakerService {
  // eslint-disable-next-line no-use-before-define
  private static instance: ReadSpeakerService;

  private subscriptions: Map<string, ReadSpeakerSubscription> = new Map();

  private scrollSubscriptions: Map<string, ScrollSubscription> = new Map();

  private activePlayerId = '';

  private startElement = document.createComment(' RSPEAK_START ');

  private constructor() {
    this.#initReadSpeaker();

    window.addEventListener(
      NAVIGATE_EVENT,
      this.#destroyActivePlayer as EventListener,
    );
  }

  public static get(): ReadSpeakerService {
    if (!ReadSpeakerService.instance) {
      ReadSpeakerService.instance = new ReadSpeakerService();
    }

    return ReadSpeakerService.instance;
  }

  public register(
    element: HTMLElement,
    callback: (event: ReadSpeakerEvent) => void,
  ): void {
    if (element instanceof HTMLElement) {
      this.subscriptions.set(element.id, { element, callback });
    }
  }

  public registerScrollContainer(
    id: string,
    element: Element,
    contentElement: Element,
  ): void {
    this.unregisterScrollContainer(id);
    const callback = () => this.#handleScrollEnd(id);
    this.scrollSubscriptions.set(id, { element, contentElement, callback });
    element.addEventListener('scrollend', callback);
  }

  public unregisterScrollContainer(id: string): void {
    const subscription = this.scrollSubscriptions.get(id);

    if (subscription) {
      subscription.element.removeEventListener(
        'scrollend',
        subscription.callback,
      );
      this.scrollSubscriptions.delete(id);
    }
  }

  public set speed(speed: number) {
    window.rspkr?.st?.set('hlspeed', speed);

    this.stop();
    this.play(this.activePlayerId);
  }

  public get speed(): number {
    return window.rspkr?.st?.get('hlspeed');
  }

  public play(playerId: string) {
    // If there is an active player, we stop and destroy this player.
    // This way the new speed and/or the RSPEAK_START position is redetected.
    this.#destroyActivePlayer();

    this.activePlayerId = playerId;

    const element = this.subscriptions.get(playerId)?.element;
    const playButtonElement: HTMLElement | null | undefined =
      element?.querySelector('.rsbtn_play');
    playButtonElement?.click();

    window.rspkr?.ui?.setActivePlayer(element);
  }

  public resume() {
    window.rspkr?.ui?.getActivePlayer()?.play();
  }

  public pause() {
    window.rspkr?.ui?.getActivePlayer()?.pause();
  }

  public stop() {
    window.rspkr?.ui?.getActivePlayer()?.stop();
  }

  #destroyActivePlayer() {
    if (window.rspkr?.ui?.getActivePlayer()) {
      window.rspkr?.ui?.getActivePlayer()?.stop();
      window.rspkr?.ui?.destroyActivePlayer();
    }
  }

  #initReadSpeaker(): void {
    const self = this; //eslint-disable-line

    window.rsConf = {
      general: {
        ...config.general,
        shadowDomSelector: [
          'tmwc-content-player',
          'tmwc-content-theory',
          'tmwc-content-view',
          'tmwc-content-interaction',
        ],
      },
      settings: { ...config.settings },
      ui: { ...config.ui },
      cb: {
        ui: {
          play() {
            self.#dispatchLocalEvent({
              element: this as unknown as HTMLElement,
              event: PlayerState.Playing,
            });
          },
          pause() {
            self.#dispatchLocalEvent({
              element: this as unknown as HTMLElement,
              event: PlayerState.Paused,
            });
          },
          stop() {
            self.#dispatchLocalEvent({
              element: this as unknown as HTMLElement,
              event: PlayerState.Stopped,
            });
          },
        },
        audio: {
          loaded() {
            self.#dispatchLocalEvent({
              element: this as unknown as HTMLElement,
              event: PlayerState.Playing,
            });
          },
          ended() {
            self.#dispatchLocalEvent({
              element: this as unknown as HTMLElement,
              event: PlayerState.Stopped,
            });
          },
          loadError() {
            self.#dispatchLocalEvent({
              element: this as unknown as HTMLElement,
              event: PlayerError.AudioLoadError,
            });
          },
          nosound() {
            self.#dispatchLocalEvent({
              element: this as unknown as HTMLElement,
              event: PlayerError.AudioNoSound,
            });
          },
        },
      },
    };

    ScriptLoader.loadScript({
      src: 'https://cdn-eu.readspeaker.com/script/5797/webReader/webReader.js?pids=wr&noDefaultSkin=1&mobile=0',
      id: 'rs_req_Init',
      async: false,
    });
  }

  #dispatchLocalEvent(event: ReadSpeakerEvent): void {
    const subscription = this.subscriptions.get(event.element.id);
    if (subscription) {
      subscription.callback(event);
    }
  }

  #handleScrollEnd = (id: string) => {
    if (this.startElement.parentNode) {
      this.startElement.parentNode.removeChild(this.startElement);
    }

    const subscription = this.scrollSubscriptions.get(id);

    if (subscription) {
      const element = this.#getFirstVisibleReadspeakerElement(subscription);
      if (element) {
        element.parentNode?.insertBefore(this.startElement, element);
      }
    }
  };

  #getFirstVisibleReadspeakerElement(
    subscription: ScrollSubscription,
  ): Element | undefined {
    const baseY = subscription.element.getBoundingClientRect().y;
    const elements =
      subscription.contentElement.querySelectorAll('*:not(.RS_SKIP)');

    if (baseY && elements) {
      return Array.from(elements).find((element) => {
        return element.getBoundingClientRect().y - baseY >= 0;
      });
    }

    return undefined;
  }
}
